<template>
  <div class="auth-form-container">
    <div class="auth-card animate__animated animate__fadeIn">
      <div class="auth-header">
        <h2>{{ isLogin ? $t("login.title") : $t("register.title") }}</h2>
        <p class="auth-subtitle">Popworld Tourguide Platform</p>
      </div>

      <el-form
        :model="authForm"
        label-position="top"
        @submit.prevent="handleSubmit"
        class="modern-form"
      >
        <el-form-item :label="$t('login.username')">
          <el-input
            v-model="authForm.username"
            :placeholder="
              !isLogin ? $t('login.usernamePlaceholder') : '请输入用户名'
            "
            size="large"
            clearable
          />
          <span v-if="usernameError" class="error-message-tip">{{
            usernameError
          }}</span>
        </el-form-item>

        <el-form-item :label="$t('login.password')">
          <el-input
            v-model="authForm.password"
            type="password"
            :placeholder="
              !isLogin ? $t('login.passwordPlaceholder') : '请输入密码'
            "
            show-password
            size="large"
          />
          <span v-if="passwordError" class="error-message-tip">{{
            passwordError
          }}</span>
        </el-form-item>

        <template v-if="!isLogin">
          <el-form-item label="确认密码">
            <el-input
              v-model="authForm.confirmPassword"
              type="password"
              placeholder="请再次输入密码"
              show-password
              size="large"
            />
          </el-form-item>

          <el-form-item label="邮箱">
            <div class="captcha-input-group">
              <el-input
                v-model="authForm.email"
                placeholder="请输入邮箱"
                size="large"
              />
              <el-button
                type="primary"
                @click="sendCaptcha(1)"
                :disabled="isCountdown"
                class="captcha-btn"
                size="large"
              >
                {{ countdown > 0 ? `${countdown}s` : "获取验证码" }}
              </el-button>
            </div>
          </el-form-item>

          <el-form-item label="验证码">
            <el-input
              v-model="authForm.captcha"
              placeholder="请输入 6 位验证码"
              size="large"
              maxlength="6"
            />
          </el-form-item>
        </template>

        <div class="form-actions">
          <el-button
            type="primary"
            native-type="submit"
            size="large"
            class="submit-btn"
          >
            {{ isLogin ? $t("login.submit") : $t("register.submit") }}
          </el-button>
        </div>
      </el-form>

      <div class="auth-helper-links">
        <span @click="toggleAuthMode" class="helper-link">
          {{ isLogin ? $t("register.switch") : $t("login.switch") }}
        </span>
        <el-divider direction="vertical" />
        <span @click="showPage" class="helper-link text-muted">忘记密码</span>
      </div>
    </div>
  </div>

  <el-dialog
    v-model="centerDialogVisible"
    title="忘记密码"
    width="460px"
    destroy-on-close
    align-center
    class="forgot-password-dialog"
  >
    <el-form :model="form" label-width="auto" label-position="top">
      <el-form-item label="邮箱：">
        <el-input
          v-model="form.email"
          placeholder="请输入绑定的邮箱"
          size="large"
        />
      </el-form-item>

      <el-form-item label="验证码：">
        <div class="captcha-input-group">
          <el-input v-model="form.code" placeholder="输入验证码" size="large" />
          <el-button
            type="primary"
            @click="sendCaptcha(2)"
            :disabled="isCountdown2"
            class="captcha-btn"
            size="large"
          >
            {{ countdown2 > 0 ? `${countdown2}s` : "获取验证码" }}
          </el-button>
        </div>
      </el-form-item>

      <el-form-item label="新密码：">
        <el-input
          v-model="form.password"
          type="password"
          show-password
          placeholder="输入新密码 (6位以上)"
          size="large"
        />
      </el-form-item>

      <el-form-item label="确认密码：">
        <el-input
          v-model="form.confirmPassword"
          type="password"
          show-password
          placeholder="再次输入新密码"
          size="large"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button size="large" @click="centerDialogVisible = false"
          >取消</el-button
        >
        <el-button type="primary" size="large" @click="editPassword"
          >确认重置</el-button
        >
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { ElMessage } from "element-plus";

export default defineComponent({
  name: "LoginRegister",
  data(): any {
    return {
      isLogin: true,
      authForm: {
        username: "",
        password: "",
        email: "",
        captcha: "",
        confirmPassword: "",
      },
      form: {
        code: "",
        password: "",
        confirmPassword: "",
        email: "",
      },
      countdown: 0,
      countdown2: 0,
      countdownInterval: null,
      countdownInterval2: null,
      usernameError: "",
      passwordError: "",
      centerDialogVisible: false,
    };
  },
  computed: {
    isCountdown() {
      return this.countdown > 0;
    },
    isCountdown2() {
      return this.countdown2 > 0;
    },
  },
  methods: {
    handleSubmit() {
      // 如果你想启用正则格式校验，可以解封 validateForm 并在 handleSubmit 第一步调用它
      // if (!this.validateForm()) { return; }

      if (this.isLogin) {
        this.$store
          .dispatch("user/login", this.authForm)
          .then(() => {
            this.$router.push("/");
          })
          .catch((err: any) => {});
      } else {
        if (this.authForm.password === this.authForm.confirmPassword) {
          this.$store
            .dispatch("user/register", this.authForm)
            .then(() => {
              this.$router.push("/");
            })
            .catch((err: any) => {});
        } else {
          ElMessage.error("两次输入的密码不一致");
        }
      }
    },
    validateForm() {
      const usernamePattern = /^[a-zA-Z0-9]{6,9}$/;
      const passwordPattern = /^[a-zA-Z0-9]{6,9}$/;

      this.usernameError = "";
      this.passwordError = "";

      if (!usernamePattern.test(this.authForm.username)) {
        this.usernameError = "用户名必须为6到9个非特殊符号的字符";
      }

      if (!passwordPattern.test(this.authForm.password)) {
        this.passwordError = "密码必须为6到9个非特殊符号的字符";
      }

      return !this.usernameError && !this.passwordError;
    },
    toggleAuthMode() {
      this.isLogin = !this.isLogin;
      this.authForm = {
        username: "",
        password: "",
        email: "",
        captcha: "",
        confirmPassword: "",
      };
      this.usernameError = "";
      this.passwordError = "";
    },
    sendCaptcha(options: number) {
      const reg =
        /^([a-zA-Z0-9_-])+@([a-zA-Z0-9_-])+((\.[a-zA-Z0-9_-]{2,3}){1,2})$/;

      if (options == 1) {
        if (!this.authForm.email) {
          ElMessage.error("请输入邮箱地址");
          return;
        }

        if (!reg.test(this.authForm.email)) {
          ElMessage.error("邮箱格式不正确");
          return;
        }

        this.$store
          .dispatch("user/GetEmailCode", {
            action: "注册账号",
            userName: this.authForm.email,
          })
          .catch((err: any) => {});

        this.countdown = 60;
        this.countdownInterval = setInterval(() => {
          if (this.countdown > 0) {
            this.countdown -= 1;
          } else {
            clearInterval(this.countdownInterval);
          }
        }, 1000);
      } else if (options == 2) {
        if (!this.form.email) {
          ElMessage.error("请输入邮箱地址");
          return;
        }

        if (!reg.test(this.form.email)) {
          ElMessage.error("邮箱格式不正确");
          return;
        }

        this.$store
          .dispatch("user/GetEmailCode", {
            action: "忘记密码",
            userName: this.form.email,
          })
          .catch((err: any) => {});

        this.countdown2 = 60;
        this.countdownInterval2 = setInterval(() => {
          if (this.countdown2 > 0) {
            this.countdown2 -= 1;
          } else {
            clearInterval(this.countdownInterval2);
          }
        }, 1000);
      }
    },
    showPage() {
      this.centerDialogVisible = true;
    },
    editPassword() {
      if (this.form.password !== this.form.confirmPassword) {
        ElMessage.error("两次密码不一致");
        return;
      }

      if (this.form.confirmPassword.length < 6) {
        ElMessage.error("密码长度必须大于6位");
        return;
      }

      this.$store
        .dispatch("user/forgetPassword", {
          code: this.form.code,
          email: this.form.email,
          newPassword: this.form.confirmPassword,
        })
        .then(() => {
          ElMessage.success("密码修改成功，请重新登录");
          this.centerDialogVisible = false;
        })
        .catch((err: any) => {});
    },
  },
  // 【安全优化】：离开页面时，两个定时器全部销毁，杜绝内存泄漏
  beforeUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.countdownInterval2) {
      clearInterval(this.countdownInterval2);
    }
  },
});
</script>

<style scoped lang="less">
/* 背景渐变加微粒艺术背景 */
.auth-form-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4ebf5 100%);
  box-sizing: border-box;
}

/* 精致轻奢卡片 */
.auth-card {
  width: 100%;
  max-width: 420px;
  padding: 40px;
  border-radius: 20px;
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 12px 40px rgba(163, 177, 198, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-sizing: border-box;

  .auth-header {
    text-align: center;
    margin-bottom: 30px;

    h2 {
      margin: 0 0 6px 0;
      font-size: 24px;
      font-weight: 700;
      color: #2c3e50;
    }

    .auth-subtitle {
      margin: 0;
      font-size: 13px;
      color: #909399;
      letter-spacing: 0.5px;
    }
  }
}

/* 表单细节美化 */
.modern-form {
  :deep(.el-form-item) {
    margin-bottom: 20px;

    .el-form-item__label {
      font-weight: 600;
      color: #4a5568;
      padding-bottom: 6px;
    }

    .el-input__wrapper {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02) inset;
      border-radius: 8px;
    }
  }
}

/* 验证码弹性组合 */
.captcha-input-group {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;

  .el-input {
    flex: 1;
  }

  .captcha-btn {
    min-width: 110px;
    height: 40px;
    border-radius: 8px;
  }
}

/* 提交大按钮 */
.form-actions {
  margin-top: 30px;

  .submit-btn {
    width: 100%;
    height: 44px;
    font-size: 15px;
    font-weight: bold;
    border-radius: 8px;
    background: #2c3e50;
    border-color: #2c3e50;
    box-shadow: 0 4px 12px rgba(44, 62, 80, 0.2);
    transition: all 0.2s ease;

    &:hover {
      background: #1a252f;
      border-color: #1a252f;
      transform: translateY(-1px);
    }
  }
}

/* 辅助操作链链接 */
.auth-helper-links {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  font-size: 13px;

  .helper-link {
    cursor: pointer;
    color: #409eff;
    transition: color 0.2s;
    user-select: none;

    &:hover {
      color: #66b1ff;
      text-decoration: underline;
    }

    &.text-muted {
      color: #909399;

      &:hover {
        color: #303133;
      }
    }
  }

  .el-divider {
    border-color: #dcdfe6;
    margin: 0 12px;
  }
}

/* 红色错误提示文字 */
.error-message-tip {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

/* 弹窗中的验证码排版修正 */
.forgot-password-dialog {
  :deep(.el-dialog__header) {
    padding-bottom: 15px;
    border-bottom: 1px solid #f2f2f2;
    .el-dialog__title {
      font-weight: bold;
    }
  }
  :deep(.el-dialog__body) {
    padding: 24px 30px;
  }
  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
}
</style>
